import { isDirectProduct } from '@features/direct-payment/DirectCheckoutSheet';
import { Package } from '@phosphor-icons/react';
import { LayoutGroup } from 'framer-motion';
import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';

import {
  groupProductVariants,
  useGetCategoriesQuery,
  useGetProductsQuery,
  type ProductDto,
  type ProductListing,
  type ProductVariant,
} from '@entities/product';
import { useGetShopServicesQuery } from '@entities/shopService';
import { useGetGiveawayQuery } from '@entities/giveaway';
import { useGetFlashDrawQuery } from '@entities/flashDraw';
import { useGetMeQuery } from '@entities/user';
import { ProductSheet } from '@features/buy-product';
import {
  ConfettiCelebration,
  useStorefrontCelebration,
} from '@features/storefront-celebration';
import { EmptyState } from '@shared/ui/EmptyState';
import { InsufficientBalanceSheet } from '@shared/ui/InsufficientBalanceSheet';
import { Screen } from '@shared/ui/Screen';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useGetPublicShopQuery } from '@entities/shop/api/shopApi';
import { MarketplaceFrame, type MarketFilters } from './MarketplaceFrame';
import { filterListings } from './marketplaceFilters';
import { PageSkeleton, SectionSkeleton } from '@widgets/PageSkeleton/PageSkeleton';
import { getProductLaunchTarget, haptics } from '@shared/lib/telegram';
import { ProductGrid } from '@widgets/ProductGrid';
import { FlashDealHero } from '@widgets/FlashDealHero';
import { ExclusiveOfferCountdown } from '@widgets/ExclusiveOfferCountdown/ExclusiveOfferCountdown';
import { EXCLUSIVE_CAMPAIGN_ENDS_AT } from '@shared/config/exclusiveCampaign';
import { FlashDrawHero } from '@widgets/FlashDrawHero';
import { GiveawayHero } from '@widgets/GiveawayHero';
import { SupportFab } from '@widgets/SupportFab';



export function StorePage() {
  const navigate = useNavigate();
  const { shopSlug } = useParams();
  const { data: shop, isLoading: shopLoading, isError: shopError, refetch: refetchShop } = useGetPublicShopQuery(shopSlug ?? '', { skip: !shopSlug });
  const [params, setParams] = useSearchParams();
  const filters: MarketFilters = { query: params.get('q') ?? '', category: params.get('category') ?? '', sort: params.get('sort') ?? 'recommended', min: params.get('min') ?? '', max: params.get('max') ?? '', available: params.get('available') === '1' };
  const changeFilter = (key: keyof MarketFilters | 'reset', value: string | boolean) => {
    setParams(previous => { const next = new URLSearchParams(previous); const name = key === 'query' ? 'q' : key;
      if (key === 'reset') { for (const field of ['q', 'category', 'min', 'max', 'available']) next.delete(field); return next; }
      if (value === false || value === '') next.delete(name); else next.set(name, value === true ? '1' : String(value)); return next;
    }, { replace: true });
  };
  const pendingLaunchTarget = useRef(getProductLaunchTarget());
  const celebration = useStorefrontCelebration();
  const { data: user } = useGetMeQuery();
  const balanceAmount = user?.balance.amount;
  const { data: catalog, isLoading, isError, refetch } = useGetProductsQuery(undefined, {
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const { data: giveawayResponse, refetch: refetchGiveaway } = useGetGiveawayQuery(undefined, {
    skip: !user,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    pollingInterval: 15_000,
  });
  const { data: flashDrawResponse, refetch: refetchFlashDraw } = useGetFlashDrawQuery(undefined, {
    skip: !user,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    pollingInterval: 5_000,
  });
  const products = useMemo(() => (catalog?.products ?? []).filter(product => !shopSlug || (shop && product.shopId === shop.id)), [catalog, shopSlug, shop]);
  const listings = useMemo(() => groupProductVariants(products), [products]);
  const query = filters.query;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedVariantIds, setSelectedVariantIds] = useState<string[]>([]);
  const [insufficientBalance, setInsufficientBalance] = useState(false);
  // Set when the customer came in through a card's Buy button rather than the
  // card itself: the sheet then opens on its confirmation step.
  const [buyNow, setBuyNow] = useState(false);

  // The field updates on every keystroke; re-filtering and re-rendering the
  // grid is allowed to lag a frame behind it. Without this the typing itself
  // was competing with a full grid re-render on the same tick.
  const deferredQuery = useDeferredValue(query);

  // Null is the "All" tab, which is also the whole storefront when the
  // operator has made no sections — the shop's behaviour before they existed.
  const categoryId = filters.category || null;
  const setCategoryId = (value: string | null) => changeFilter('category', value ?? '');
  const { data: categories = [], isLoading: categoriesLoading } = useGetCategoriesQuery();

  // Features shown as product cards — SMS today, more later. They live in
  // their own tab rather than the grid because they are not catalogue rows,
  // and the server hides any the customer may not open, so an empty list is
  // simply a tab that does not appear.
  const { data: shopServices = [] } = useGetShopServicesQuery();
  const servicesOffered = !shopSlug && shopServices.length > 0;

  // A section the operator hid or emptied while the tab was open would
  // otherwise leave the grid stuck on a filter nothing can satisfy.
  useEffect(() => {
    if (!categoriesLoading && categoryId && !categories.some((category) => category.id === categoryId)) {
      setCategoryId(null);
    }
  }, [categories, categoryId, categoriesLoading]);

  const visible = useMemo(() => filterListings(listings, { ...filters, query: deferredQuery }),
    [listings, deferredQuery, filters.category, filters.sort, filters.min, filters.max, filters.available]);

  const selected = useMemo(
    () => products.find((product) => product.id === selectedId) ?? null,
    [products, selectedId],
  );
  const selectedVariants = useMemo(
    () => {
      const variantsById = new Map<string, ProductVariant>();
      for (const listing of listings) {
        for (const variant of listing.variants) variantsById.set(variant.product.id, variant);
      }

      return selectedVariantIds
        .map((id) => variantsById.get(id))
        .filter((variant): variant is ProductVariant => Boolean(variant));
    },
    [listings, selectedVariantIds],
  );
  const selectedListingName = useMemo(
    () => {
      if (selectedVariantIds.length <= 1) return selected?.name ?? null;

      return (
        listings.find((listing) =>
          listing.variants.some((variant) => selectedVariantIds.includes(variant.product.id)),
        )?.name ?? selected?.name ?? null
      );
    },
    [listings, selected, selectedVariantIds],
  );
  const featured = useMemo(
    () => products.find((product) =>
      product.id === catalog?.featuredFlashProductId && !product.exclusive,
    ) ?? null,
    [products, catalog?.featuredFlashProductId],
  );
  const clockOffsetMs = catalog
    ? Date.parse(catalog.serverTime) - catalog.clientReceivedAt
    : 0;
  const hasExclusiveProducts = products.some((product) => product.exclusive);
  const giveawayClockOffsetMs = giveawayResponse
    ? Date.parse(giveawayResponse.serverTime) - giveawayResponse.clientReceivedAt
    : 0;
  const flashDrawClockOffsetMs = flashDrawResponse
    ? Date.parse(flashDrawResponse.serverTime) - flashDrawResponse.clientReceivedAt
    : 0;



  // Telegram product links now land on the same dedicated page as storefront clicks.
  useEffect(() => {
    const target = pendingLaunchTarget.current;
    if (!target || !catalog) return;
    pendingLaunchTarget.current = null;
    const product = products.find(item => item.id === target || item.slug === target);
    if (product) navigate(`/product/${encodeURIComponent(product.slug)}`);
  }, [catalog, products, navigate]);

  // The server does not need a cleanup job, but an open catalogue needs one
  // refresh at the first local expiry so its displayed price catches up.
  useEffect(() => {
    const nextEnd = products.reduce<number | null>((nearest, product) => {
      if (!product.flashDeal) return nearest;
      const end = Date.parse(product.flashDeal.endsAt);
      return nearest === null || end < nearest ? end : nearest;
    }, null);
    if (nextEnd === null) return;

    const delay = Math.max(50, nextEnd - (Date.now() + clockOffsetMs) + 150);
    const timeout = window.setTimeout(() => void refetch(), Math.min(delay, 2_147_000_000));
    return () => window.clearTimeout(timeout);
  }, [products, clockOffsetMs, refetch]);

  // Refresh the shared catalogue once as the collection expires, so cards,
  // prices and an already-open sheet all pick up the regular HubX cost price.
  useEffect(() => {
    if (!hasExclusiveProducts) return;
    const delay = Date.parse(EXCLUSIVE_CAMPAIGN_ENDS_AT) - (Date.now() + clockOffsetMs);
    if (delay <= 0) return;
    const timeout = window.setTimeout(() => void refetch(), delay + 100);
    return () => window.clearTimeout(timeout);
  }, [hasExclusiveProducts, clockOffsetMs, refetch]);

  useEffect(() => {
    const giveaway = giveawayResponse?.giveaway;
    if (!giveaway) return;
    const delay = Math.max(50, Date.parse(giveaway.endsAt) - (Date.now() + giveawayClockOffsetMs) + 150);
    const timeout = window.setTimeout(() => void refetchGiveaway(), Math.min(delay, 2_147_000_000));
    return () => window.clearTimeout(timeout);
  }, [giveawayResponse?.giveaway, giveawayClockOffsetMs, refetchGiveaway]);

  useEffect(() => {
    const draw = flashDrawResponse?.draw;
    if (!draw) return;
    const targets = [draw.startsAt, draw.endsAt, draw.drawAt].filter((value): value is string => Boolean(value));
    const now = Date.now() + flashDrawClockOffsetMs;
    const next = targets.map(Date.parse).filter((time) => time > now).sort((a, b) => a - b)[0];
    if (!next) return;
    const timeout = window.setTimeout(
      () => void refetchFlashDraw(),
      Math.min(Math.max(100, next - now + 250), 2_147_000_000),
    );
    return () => window.clearTimeout(timeout);
  }, [flashDrawResponse?.draw, flashDrawClockOffsetMs, refetchFlashDraw]);

  // Stable identities, so the memoised cards do not all re-render whenever
  // this page does.
  const openProduct = useCallback((listing: ProductListing) => {
    navigate(`/product/${encodeURIComponent(listing.primary.slug)}`);
  }, [navigate]);

  const openListingConfirmation = useCallback((listing: ProductListing) => {
    // Skip mounting the product sheet when the cached wallet already rules out a purchase.
    if (!isDirectProduct(listing.primary) && balanceAmount !== undefined && Number(balanceAmount) < Number(listing.primary.price.amount)) {
      setInsufficientBalance(true);
      haptics.notify('warning');
      return;
    }
    setBuyNow(true);
    setSelectedVariantIds(listing.variants.map((variant) => variant.product.id));
    setSelectedId(listing.primary.id);
  }, [balanceAmount]);

  const openConfirmation = useCallback((product: ProductDto) => {
    if (!isDirectProduct(product) && balanceAmount !== undefined && Number(balanceAmount) < Number(product.price.amount)) {
      setInsufficientBalance(true);
      haptics.notify('warning');
      return;
    }
    setBuyNow(true);
    setSelectedVariantIds([product.id]);
    setSelectedId(product.id);
  }, [balanceAmount]);

  // A successful purchase invalidates Product; closing without one changes no stock.
  const closeSheet = useCallback(() => {
    setSelectedId(null);
    setSelectedVariantIds([]);
  }, []);

  const renderCatalogResults = () => (
    <>
      {isLoading && !catalog ? <SectionSkeleton variant="products" /> : null}

      {isError ? (
        <EmptyState
          icon={Package}
          title="Could not load products"
          description="Check your connection and try again."
        />
      ) : null}

      {!isLoading && !isError && visible.length === 0 ? (
        <EmptyState
          icon={Package}
          title={listings.length > 0 ? 'No matches' : 'No products yet'}
          description={listings.length > 0 ? 'Try another search or reset your filters.' : 'Please check back soon.'}
        />
      ) : null}

      {visible.length > 0 ? (
        <ProductGrid
          products={visible}
          onSelect={openProduct}
          onBuy={openListingConfirmation}
          clockOffsetMs={clockOffsetMs}
        />
      ) : null}
    </>
  );

  if (shopSlug && shopLoading) return <Screen title="Loading shop"><PageSkeleton variant="shop" /></Screen>;
  if (shopSlug && shopError) return <Screen title="Shop unavailable"><EmptyState icon={Package} title="This shop is unavailable" description="It may not be published yet, or could not be loaded." /><button onClick={() => void refetchShop()}>Try again</button> · <Link to="/">Back to marketplace</Link></Screen>;

  return (
    <LayoutGroup id="store-search">
      <>
        <ConfettiCelebration
          runId={celebration.runId}
          onComplete={celebration.finish}
          preview={celebration.isPreview}
        />
        <Screen title={shop?.name ?? 'Digital marketplace'}>
          <MarketplaceFrame categories={categories} filters={filters} change={changeFilter} count={visible.length} total={listings.length} shop={shop} categoriesLoading={categoriesLoading && !categories.length} productsLoading={isLoading && !catalog}>
          {hasExclusiveProducts ? (
            <ExclusiveOfferCountdown clockOffsetMs={clockOffsetMs} />
          ) : null}

          {/* The heroes advertise products; the numbers tab sells none. */}
          {flashDrawResponse?.draw ? (
            <FlashDrawHero
              draw={flashDrawResponse.draw}
              clockOffsetMs={flashDrawClockOffsetMs}
            />
          ) : null}

          {giveawayResponse?.giveaway ? (
            <GiveawayHero
              giveaway={giveawayResponse.giveaway}
              clockOffsetMs={giveawayClockOffsetMs}
            />
          ) : null}

          {featured ? (
            <FlashDealHero
              product={featured}
              clockOffsetMs={clockOffsetMs}
              onBuy={openConfirmation}
            />
          ) : null}

          {renderCatalogResults()}
          {servicesOffered && <p style={{ marginTop: 24 }}><Link to="/services/sms">Explore digital numbers →</Link></p>}
          </MarketplaceFrame>
        </Screen>

        <ProductSheet
          product={selected}
          title={selectedListingName}
          variants={selectedVariants}
          onVariantSelect={setSelectedId}
          startConfirming={buyNow}
          clockOffsetMs={clockOffsetMs}
          onClose={closeSheet}
        />

        <InsufficientBalanceSheet
          open={insufficientBalance}
          source="product"
          onClose={() => setInsufficientBalance(false)}
        />

        {!selected ? <SupportFab /> : null}
      </>
    </LayoutGroup>
  );
}
